import { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, FormLabel } from '@mui/material';
import { CloseRounded, UploadRounded } from '@mui/icons-material';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';

import {
  Content,
  Section,
  Div,
  InputSection,
  Typography,
  SectionHeaderDrawer,
  PreviewArea,
} from './styles';

import { Input } from '../../../../components/Input';
import { CpfInput } from '../../../../components/CpfInput';
import { CellphoneInput } from '../../../../components/CellphoneInput';
import { LoadingBackdrop } from '../../../../components/LoadingBackdrop';

import {
  isValidCPF,
  removeDotsAndTracesCPF,
} from '../../../../utils/cpfValidation';
import { splitName } from '../../../../utils/nameHelpers';
import { acceptImageTypes } from '../../../../utils/mimeTypes';

import {
  createAvatarAndLogoRequest,
  updateUserRequest,
} from '../../../../services';

import useAuthStore from '../../../../store/auth';
import useCompanyStore from '../../../../store/companies';

interface DrawerEditMemberProps {
  setOpenDrawerAddMember: (open: boolean) => void;
}

export const DrawerEditMember = ({
  setOpenDrawerAddMember,
}: DrawerEditMemberProps) => {
  const selectCompany = useCompanyStore(state => state.selectedCompany);
  const userFromStorage = useAuthStore(state => state.user);
  const setUserFromStorage = useAuthStore(state => state.setUserAuthenticated);
  const tokens = useAuthStore(state => state.tokens);

  const [profileSelected, setProfileSelected] = useState('');
  const [url, setUrl] = useState<any>('');
  const [avatarName, setAvatarName] = useState('');
  const [avatarMimetype, setAvatarMimetype] = useState('');
  const [avatarSize, setAvatarSize] = useState(0);
  const [avatarFileInBase64, setAvatarFileInBase64] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const userInCompany = selectCompany?.users?.find(
    u => u.id === userFromStorage?.id,
  );

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      name: Yup.string().required('O campo Nome é obrigatório.'),
      surname: Yup.string().required('O campo Sobrenome é obrigatório.'),
      email: Yup.string()
        .email('Digite um e-mail válido')
        .required('O campo E-mail é obrigatório.'),
      cellphone: Yup.string().nullable(),
      linkedin: Yup.string().nullable().url('Digite um link de perfil válido'),
      cpf: Yup.string()
        .required('O campo CPF é obrigatório.')
        .min(11, 'Mínimo de 11 dígitos')
        .test('test-invalid-cpf', 'CPF inválido', cpf =>
          isValidCPF(cpf as string),
        ),
    });
  }, []);

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(formValidation()),
  });

  useEffect(() => {
    if (userFromStorage && Object.keys(userFromStorage).length > 0) {
      setUrl(userFromStorage.avatar_url);
      setValue('name', splitName(userFromStorage.name as string, 'firstName'));
      setValue('surname', splitName(userFromStorage.name as string, ''));
      setValue('email', userFromStorage.email);
      setValue('cellphone', userFromStorage.phone);
      setValue('linkedin', userFromStorage.linkedIn);
      setValue('cpf', userFromStorage.doc || '');
      setProfileSelected(userFromStorage.profile);
    }
  }, [setValue, userFromStorage]);

  const handleLogo = useCallback((t: any) => {
    const { files } = t.target;

    let reader = new FileReader();
    reader.readAsDataURL(files[0]);

    reader.onload = () => {
      setAvatarFileInBase64(reader.result);
    };
    reader.onerror = error => {
      console.log('convertFileInBase64 error => ', error);
      return null;
    };

    const fileName = files[0].name;
    const fileSize = files[0].size;
    const fileType = files[0].type;

    const newUrl = URL.createObjectURL(files[0]);

    setAvatarName(fileName);
    setAvatarMimetype(fileType);
    setAvatarSize(fileSize);

    // setFile(files[0]);
    setUrl(newUrl);
  }, []);

  const handleEditUser = useCallback(
    async (formData: any) => {
      setIsLoading(true);
      try {
        const updateUser = {
          name: `${formData.name} ${formData.surname}`,
          email: formData.email,
          phone: formData.cellphone ? formData.cellphone : null,
          doc: removeDotsAndTracesCPF(formData.cpf),
          avatar_url: userFromStorage?.avatar_url,
          companies: userFromStorage?.companies,
          companies_ids: userFromStorage?.companies_ids,
          status: userFromStorage?.status,
          profile: profileSelected,
          linkedIn: formData.linkedin ? formData.linkedin : null,
        };
        const arquiveData = {
          original_name: avatarName,
          mimetype: avatarMimetype,
          size: avatarSize,
          localFile: avatarFileInBase64,
        };

        if (userFromStorage?.avatar_url !== url) {
          if (url !== userFromStorage?.avatar_url && url !== '') {
            const imageResponse = await createAvatarAndLogoRequest(
              arquiveData as any,
              userFromStorage?.id as string,
              selectCompany?.id as string,
            );

            if (imageResponse?.status === 201) {
              const data = {
                ...updateUser,
                avatar_url: imageResponse?.data?.url,
              };

              const response = await updateUserRequest(
                userFromStorage?.id as string,
                data as any,
              );

              if (response?.status === 200) {
                setUserFromStorage({ ...response?.data }, tokens!);
                toast.success('Usuário editado com sucesso');
                setOpenDrawerAddMember(false);
              }
            }
          }
        } else {
          const response = await updateUserRequest(
            userFromStorage?.id as string,
            updateUser as any,
          );

          if (response?.status === 200) {
            setUserFromStorage({ ...response?.data }, tokens!);
            toast.success('Usuário editado com sucesso');
            setOpenDrawerAddMember(false);
          }
        }
      } catch (err) {
        console.log('Error -> Dados pessoais -> handleEditUser => ', err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      selectCompany?.id,
      userFromStorage?.avatar_url,
      userFromStorage?.companies,
      userFromStorage?.companies_ids,
      userFromStorage?.status,
      profileSelected,
      userFromStorage?.id,
      setOpenDrawerAddMember,
      avatarName,
      avatarMimetype,
      avatarSize,
      avatarFileInBase64,
      url,
      setUserFromStorage,
      tokens,
    ],
  );

  const validateUser = () => {
    if (userInCompany?.profile === 'UN1' || userInCompany?.profile === 'UN2') {
      return false;
    }
    if (window.location.href.includes('dados-pessoais')) {
      return false;
    }
    return true;
  };

  return (
    <Box sx={{ width: '560px', height: '100%' }} role="presentation">
      {isLoading && (
        <LoadingBackdrop
          text="Salvando suas informações"
          component=""
          loading={isLoading}
        />
      )}
      <Content>
        <SectionHeaderDrawer>
          <CloseRounded
            sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
            onClick={() => setOpenDrawerAddMember(false)}
          />
          <Typography>Editar Usuário</Typography>
        </SectionHeaderDrawer>
        <form onSubmit={handleSubmit(handleEditUser)}>
          <Section>
            <Div className="content-Edit">
              <InputSection>
                {userFromStorage?.id && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'start',
                        marginBottom: '10px',
                      }}
                    >
                      {url && (
                        <PreviewArea
                          style={{
                            width: '30%%',
                            marginBottom: 20,
                          }}
                          sx={{ borderRadius: '50%' }}
                        >
                          <img src={url} alt="logo" />
                        </PreviewArea>
                      )}

                      <FormLabel
                        htmlFor="input-file"
                        className="add-logo"
                        sx={{
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="file"
                          name="file"
                          onChange={handleLogo}
                          id="input-file"
                          style={{
                            display: 'none',
                          }}
                          accept={acceptImageTypes()}
                        />
                        <UploadRounded /> {url ? 'Alterar' : 'Inserir'} foto de
                        perfil
                      </FormLabel>
                    </Box>
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      name="name"
                      type="text"
                      label="Nome"
                      control={control}
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      name="surname"
                      label="Sobrenome"
                      type="text"
                      control={control}
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      label="E-mail"
                      name="email"
                      disabled
                      control={control}
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      label="Celular"
                      name="cellphone"
                      control={control}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{ inputComponent: CellphoneInput as any }}
                    />
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      label="CPF"
                      name="cpf"
                      required
                      disabled
                      control={control}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{ inputComponent: CpfInput as any }}
                    />
                    <Input
                      className="inputMember"
                      id="outlined-basic"
                      label="LinkedIn"
                      name="linkedin"
                      control={control}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </>
                )}
              </InputSection>
            </Div>
            <Box width="100%" display="flex" justifyContent="end">
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={validateUser()}
                sx={{
                  width: '197px',
                  height: '42px',
                  color: '#FFFFFF',
                  fontSize: '15px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                }}
              >
                Salvar Alterações
              </Button>
            </Box>
          </Section>
        </form>
      </Content>
    </Box>
  );
};
